import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import { useContext, useState } from "react";
import { submitSocialCampaign } from "screens/Campaigns/CampaignService";
import { convertToEstTime, handleErrorMessage } from "helpers/methods";
import moment from "moment";
import { cloneDeep } from "lodash";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { SocialCampaignContext } from "../Context";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import DateTimePicker from "../../components/DateTimePicker";

interface CustomProps {
  isDialogOpen: boolean;
  handleDialogClose: Function;
}

const Schedule = (props: CustomProps) => {
  const { submitCampaign } = useContext(SocialCampaignContext);
  const [formFields, setFormFields] = useState<{
    scheduleType: string;
    date: { value: Date | null; error: string };
    time: { value: Date | null; error: string };
  }>({
    scheduleType: "send-now",
    date: {
      value: null,
      error: "",
    },
    time: {
      value: null,
      error: "",
    },
  });
  const [submittingCampaign, setSubmittingCampaign] = useState<boolean>(false);

  const handleValidation = () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    const now = moment();
    const selectedDate = clonedFormFields.date.value
      ? moment(clonedFormFields.date.value)
      : null;
    const selectedTime = clonedFormFields.time.value
      ? moment(clonedFormFields.time.value, "HH:mm")
      : null;
    if (clonedFormFields.scheduleType === "send-later") {
      if (!clonedFormFields.date.value) {
        error = true;
        clonedFormFields.date.error = "Please select the date.";
      }
      if (moment().isAfter(clonedFormFields.date.value, "date")) {
        error = true;
        clonedFormFields.date.error =
          "Selected date is already past. Please select a future date.";
      }
      if (!clonedFormFields.time.value) {
        error = true;
        clonedFormFields.time.error = "Please select the time.";
      }
      if (!selectedTime) {
        error = true;
        clonedFormFields.time.error = "Please select the time.";
      } else if (selectedDate && selectedDate.isSame(now, "day")) {
        const selectedDateTime = moment(
          `${selectedDate.format("YYYY-MM-DD")} ${selectedTime.format("HH:mm")}`
        );

        if (selectedDateTime.isBefore(now)) {
          error = true;
          clonedFormFields.time.error =
            "Selected time is in the past. Please select a future time.";
        }
      }
    }
    if (error) {
      setFormFields(clonedFormFields);
      return error;
    }
    return error;
  };

  const runCampaign = async () => {
    try {
      if (handleValidation()) {
        return;
      }
      setSubmittingCampaign(true);
      let scheduleTime = "";
      if (formFields.scheduleType === "send-later") {
        const date = new Date(formFields.date.value!);
        const time = new Date(formFields.time.value!);
        scheduleTime = moment(date)
          .set({
            hour: time.getHours(),
            minute: time.getMinutes(),
            second: time.getSeconds(),
          })
          .toString();
        scheduleTime = convertToEstTime(scheduleTime, "MM/DD/yyyy HH:mm:ss");
      }
      await submitSocialCampaign(submitCampaign, scheduleTime);
      setSubmittingCampaign(false);
      props.handleDialogClose();
      history.push(
        `${
          viewpaths.campaignsViewPath
        }?page=${1}&rowsPerPage=${10}&status=Submitted&category=all&tab=social`
      );
    } catch (error: any) {
      setSubmittingCampaign(false);
      handleErrorMessage(error);
    }
  };

  const getBody = () => {
    return (
      <Stack spacing={3}>
        <Typography>When would you like to send the campaign?</Typography>
        <RadioGroup
          value={formFields.scheduleType}
          onChange={(event: any) => {
            const newScheduleType = event.target.value;
            const now = new Date();
            setFormFields({
              ...formFields,
              scheduleType: newScheduleType,
              date: {
                value: newScheduleType === "send-later" ? now : null,
                error: "",
              },
              time: {
                value:
                  newScheduleType === "send-later"
                    ? new Date(now.getTime() + 5 * 60000)
                    : null,
                error: "",
              },
            });
          }}
        >
          <Stack spacing={2}>
            <FormControlLabel
              value="send-now"
              control={<Radio size="small" />}
              label={
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Send now
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Campaign will be sent immediately
                  </Typography>
                </Stack>
              }
            />
            <FormControlLabel
              value="send-later"
              control={<Radio size="small" />}
              label={
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Schedule for later
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Campaign will be sent at the set time
                  </Typography>
                </Stack>
              }
            />
          </Stack>
        </RadioGroup>
        {formFields.scheduleType === "send-later" && (
          <DateTimePicker
            formFields={formFields}
            setFormFields={setFormFields}
          />
        )}
      </Stack>
    );
  };

  const handleDialogClose = () => {
    setFormFields({
      scheduleType: "send-now",
      date: {
        value: null,
        error: "",
      },
      time: {
        value: null,
        error: "",
      },
    });
    props.handleDialogClose();
  };

  const getFooter = () => {
    return (
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleDialogClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={runCampaign}
          disabled={formFields.scheduleType === ""}
        >
          {formFields.scheduleType === "send-later" ? "Schedule" : "Send Now"}
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomDialog
        handleDialogClose={handleDialogClose}
        isDialogOpen={props.isDialogOpen}
        body={getBody()}
        footer={getFooter()}
        title={
          <Typography variant="h4" color="#7551D0">
            Schedule
          </Typography>
        }
        closeButtonVisibility
        closable
        persistOnClickOutside
        persistOnEsc
      />
      <CustomLoader isLoading={submittingCampaign} />
    </>
  );
};

export default Schedule;
